import {
  $,
  api,
  appState,
  SESSION_KEY,
  type SavedSession,
} from './app-state';
import { getCurrentServerUrl } from '../shared/constants';
import { loadDevices } from './devices';
import type {
  AuthSession,
  AuthSessionsResult,
  SecurityActivityResult,
} from '../shared/electron-api';
import { syncPersistedFileTasks } from './file-transfer';
import { validateAccountPassword, validateAccountUsername } from '../shared/auth-policy';

function saveSession(): void {
  try {
    localStorage.setItem(SESSION_KEY, JSON.stringify({
      token: api.token,
      ...(api.refreshToken ? { refreshToken: api.refreshToken } : {}),
      username: api.username,
    } satisfies SavedSession));
  } catch {
    // 浏览器存储不可用时仍允许当前页面继续使用会话。
  }
}

function clearSession(): void {
  try {
    localStorage.removeItem(SESSION_KEY);
  } catch {
    // ignore
  }
  api.serverUrl = getCurrentServerUrl();
  api.token = '';
  api.refreshToken = '';
  api.username = '';
}

function readSession(): SavedSession | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const value = JSON.parse(raw) as Partial<SavedSession>;
    if (
      typeof value.token !== 'string' ||
      typeof value.username !== 'string' ||
      !value.token ||
      !value.username
    ) {
      localStorage.removeItem(SESSION_KEY);
      return null;
    }
    return {
      token: value.token,
      refreshToken: typeof value.refreshToken === 'string' ? value.refreshToken : '',
      username: value.username,
    };
  } catch {
    return null;
  }
}

function setAccountState(label: string, state: 'ready' | 'warn' | 'error' | 'neutral'): void {
  for (const id of ['topbar-state-dot', 'panel-state-dot']) {
    const dot = $(id);
    dot.className = `state-dot ${state === 'ready' ? '' : state}`.trim();
  }
  $('topbar-state-label').textContent = label;
  $('panel-state-label').textContent = label;
}

function setAccountTab(): void {
  document.querySelectorAll<HTMLButtonElement>('.tab').forEach((tab) => {
    const active = tab.dataset.tab === 'account';
    tab.classList.toggle('active', active);
    tab.setAttribute('aria-selected', String(active));
    tab.tabIndex = active ? 0 : -1;
    const panel = document.getElementById(`tab-${tab.dataset.tab || ''}`);
    if (panel) {
      panel.classList.toggle('hidden', !active);
      panel.setAttribute('aria-hidden', String(!active));
    }
  });
}

function renderLoggedOut(message = '', messageKind: 'info' | 'ok' | 'error' = 'info'): void {
  if (appState.devicesRefreshTimer !== null) window.clearTimeout(appState.devicesRefreshTimer);
  appState.devicesRefreshTimer = null;
  $('account-form').classList.remove('hidden');
  $('account-session').classList.add('hidden');
  $('devices-pane').classList.add('hidden');
  $('account-session-msg').textContent = '';
  $('security-activity-panel').classList.add('hidden');
  document.getElementById('account-security-login-hint')?.classList.remove('hidden');
  document.getElementById('account-security-content')?.classList.add('hidden');
  document.getElementById('auth-sessions-panel')?.classList.add('hidden');
  document.getElementById('account-danger-zone')?.classList.add('hidden');
  $('security-activity-list').replaceChildren();
  $('security-activity-msg').textContent = '';
  $('acc-msg').textContent = message;
  $('acc-msg').className = message ? `status ${messageKind === 'info' ? '' : messageKind}`.trim() : 'status';
  $('account-state-badge').textContent = '未登录';
  setAccountState('未登录', 'neutral');
}

function renderLoggedIn(message = '', state: 'ready' | 'warn' = 'ready'): void {
  $('account-form').classList.add('hidden');
  $('account-session').classList.remove('hidden');
  $('account-name').textContent = api.username || '已登录账号';
  $('account-avatar').textContent = (api.username || '?').trim().slice(0, 1).toUpperCase();
  $('account-state-badge').textContent = state === 'warn' ? '待验证' : '已登录';
  $('account-state-badge').classList.toggle('warn', state === 'warn');
  document.getElementById('account-security-login-hint')?.classList.add('hidden');
  document.getElementById('account-security-content')?.classList.remove('hidden');
  document.getElementById('account-danger-zone')?.classList.remove('hidden');
  $('account-session-msg').textContent = message;
  $('account-session-msg').className = state === 'warn' ? 'status warn' : 'status ok';
  setAccountState(state === 'warn' ? '已登录 · 待验证' : '已登录', state);
}

function formatAuthSessionTime(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return '时间未知';
  return new Date(value).toLocaleString(undefined, {
    month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function createAccountIcon(symbol: string): SVGSVGElement {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('class', 'ic');
  svg.setAttribute('aria-hidden', 'true');
  const use = document.createElementNS('http://www.w3.org/2000/svg', 'use');
  use.setAttribute('href', `#icon-${symbol}`);
  svg.append(use);
  return svg;
}

function authSessionIcon(session: AuthSession): string {
  if (session.client === 'desktop') return 'device';
  if (session.client === 'web') return 'browser';
  return 'user';
}

function renderAuthSessions(result: AuthSessionsResult): void {
  const list = $('auth-sessions-list');
  const message = $('auth-sessions-msg');
  const revokeOthers = $('auth-sessions-revoke-others') as HTMLButtonElement;
  list.replaceChildren();
  message.textContent = result.ok ? '' : result.error || '登录设备暂时无法加载';
  message.className = `status${result.ok ? '' : ' err'}`;
  revokeOthers.disabled = !result.ok || result.sessions.filter((session) => !session.current).length === 0;
  if (!result.ok) return;
  if (result.sessions.length === 0) {
    list.textContent = '当前登录方式较旧，暂时没有可展示的登录设备';
    return;
  }
  for (const session of result.sessions) {
    const row = document.createElement('div');
    row.className = 'auth-session-row';
    const mark = document.createElement('span');
    mark.className = 'auth-session-mark';
    mark.append(createAccountIcon(authSessionIcon(session)));
    const copy = document.createElement('div');
    copy.className = 'auth-session-copy';
    const title = document.createElement('strong');
    title.textContent = session.label || '登录设备';
    const meta = document.createElement('span');
    meta.textContent = `最近使用 ${formatAuthSessionTime(session.lastSeen)} · 登录于 ${formatAuthSessionTime(session.createdAt)}`;
    copy.append(title, meta);
    row.append(mark, copy);
    if (session.current) {
      const current = document.createElement('span');
      current.className = 'auth-session-current';
      current.textContent = '当前设备';
      row.append(current);
    } else {
      const revoke = document.createElement('button');
      revoke.type = 'button';
      revoke.className = 'auth-session-revoke';
      revoke.textContent = '退出';
      revoke.setAttribute('aria-label', `退出 ${session.label || '登录设备'}`);
      revoke.addEventListener('click', async () => {
        if (!window.confirm(`退出“${session.label || '这台设备'}”的登录？`)) return;
        revoke.disabled = true;
        revoke.textContent = '退出中…';
        const action = await api.revokeAuthSession(session.id);
        if (!action.ok) {
          message.textContent = action.error || '退出设备失败';
          message.className = 'status err';
          revoke.disabled = false;
          revoke.textContent = '退出';
          return;
        }
        await loadAuthSessions();
      });
      row.append(revoke);
    }
    list.append(row);
  }
}

async function loadAuthSessions(): Promise<void> {
  const panel = $('auth-sessions-panel');
  if (!api.token) {
    panel.classList.add('hidden');
    return;
  }
  panel.classList.remove('hidden');
  renderAuthSessions(await api.listAuthSessions());
}

function securityEventLabel(type: string): string {
  const labels: Record<string, string> = {
    register: '注册账号',
    login: '登录成功',
    logout: '退出登录',
    'session-revoked': '退出一个登录设备',
    'sessions-revoked': '退出其他登录设备',
    'device-added': '添加设备',
    'device-removed': '移除设备',
  };
  return labels[type] || '账号活动';
}

function securityEventClient(client: string): string {
  if (client === 'desktop') return '桌面应用';
  if (client === 'web') return '浏览器';
  return '其他方式';
}

function securityEventIcon(type: string): string {
  if (type === 'login' || type === 'register') return 'login';
  if (type === 'logout' || type === 'session-revoked' || type === 'sessions-revoked') return 'logout';
  if (type === 'device-added') return 'plus';
  if (type === 'device-removed') return 'trash';
  return 'more';
}

function formatSecurityEventTime(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return '时间未知';
  return new Date(value).toLocaleString(undefined, {
    year: 'numeric', month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function renderSecurityActivity(result: SecurityActivityResult): void {
  const list = $('security-activity-list');
  const message = $('security-activity-msg');
  list.replaceChildren();
  message.textContent = result.ok ? '' : result.error || '最近活动暂时无法加载';
  message.className = `status${result.ok ? '' : ' err'}`;
  if (!result.ok) return;
  if (result.events.length === 0) {
    list.textContent = '暂时没有可展示的活动';
    return;
  }
  for (const event of result.events) {
    const row = document.createElement('div');
    row.className = 'security-activity-row';
    const mark = document.createElement('span');
    mark.className = 'security-activity-mark';
    mark.append(createAccountIcon(securityEventIcon(event.type)));
    const copy = document.createElement('div');
    copy.className = 'security-activity-copy';
    const title = document.createElement('strong');
    title.textContent = securityEventLabel(event.type);
    const meta = document.createElement('span');
    meta.textContent = `${securityEventClient(event.client)} · ${formatSecurityEventTime(event.createdAt)}`;
    copy.append(title, meta);
    row.append(mark, copy);
    list.append(row);
  }
}

async function loadSecurityActivity(): Promise<void> {
  const panel = $('security-activity-panel');
  if (!api.token) {
    panel.classList.add('hidden');
    return;
  }
  panel.classList.remove('hidden');
  const button = $('security-activity-refresh') as HTMLButtonElement;
  button.disabled = true;
  try {
    renderSecurityActivity(await api.listSecurityActivity());
    // requestJson may have refreshed the access/refresh token pair.
    saveSession();
  } finally {
    button.disabled = false;
  }
}

async function revokeOtherAuthSessions(): Promise<void> {
  const button = $('auth-sessions-revoke-others') as HTMLButtonElement;
  if (button.disabled || !window.confirm('退出其他设备的登录？当前设备不会受到影响。')) return;
  button.disabled = true;
  const result = await api.revokeOtherAuthSessions();
  const message = $('auth-sessions-msg');
  if (!result.ok) {
    message.textContent = result.error || '退出其他设备失败';
    message.className = 'status err';
  } else {
    message.textContent = result.revoked ? `已退出 ${result.revoked} 台设备` : '没有其他登录设备';
    message.className = 'status ok';
  }
  await loadAuthSessions();
}

function setAuthBusy(busy: boolean, mode?: 'login' | 'register'): void {
  const login = $('acc-login-btn') as HTMLButtonElement;
  const register = $('acc-register-btn') as HTMLButtonElement;
  login.disabled = busy;
  register.disabled = busy;
  login.setAttribute('aria-busy', String(busy && mode === 'login'));
  register.setAttribute('aria-busy', String(busy && mode === 'register'));
  login.textContent = busy && mode === 'login' ? '登录中…' : '登录';
  register.textContent = busy && mode === 'register' ? '注册中…' : '注册账号';
}

function accountLogout(message = '已退出登录', messageKind: 'info' | 'ok' | 'error' = 'ok'): void {
  void api.logout();
  if (appState.devicesRefreshTimer !== null) window.clearTimeout(appState.devicesRefreshTimer);
  appState.devicesRefreshTimer = null;
  clearSession();
  ($('acc-pass') as HTMLInputElement).value = '';
  renderLoggedOut(message, messageKind);
  setAccountTab();
}

async function accountAuth(mode: 'login' | 'register'): Promise<void> {
  if (appState.authBusy) return;
  const user = ($('acc-user') as HTMLInputElement).value.trim();
  const pass = ($('acc-pass') as HTMLInputElement).value;
  const msg = $('acc-msg');
  const usernameError = validateAccountUsername(user, mode);
  if (usernameError) {
    msg.textContent = usernameError;
    msg.className = 'status err';
    ($('acc-user') as HTMLInputElement).setAttribute('aria-invalid', 'true');
    ($('acc-user') as HTMLInputElement).focus();
    return;
  }
  ($('acc-user') as HTMLInputElement).removeAttribute('aria-invalid');
  const passwordError = validateAccountPassword(pass, user, mode);
  if (passwordError) {
    msg.textContent = passwordError;
    msg.className = 'status err';
    ($('acc-pass') as HTMLInputElement).setAttribute('aria-invalid', 'true');
    ($('acc-pass') as HTMLInputElement).focus();
    return;
  }
  ($('acc-pass') as HTMLInputElement).removeAttribute('aria-invalid');
  appState.authBusy = true;
  setAuthBusy(true, mode);
  msg.textContent = mode === 'register' ? '注册中…' : '登录中…';
  msg.className = 'status';
  const abort = new AbortController();
  const timeout = window.setTimeout(() => abort.abort(), 8_000);
  try {
    api.serverUrl = getCurrentServerUrl();
    const r = await fetch(`${api.serverUrl}/api/auth/${mode}`, {
      method: 'POST',
      headers: { 'content-type': 'application/json', 'X-LinkDesk-Client': 'web' },
      body: JSON.stringify({ username: user, password: pass }),
      signal: abort.signal,
    });
    const data = await r.json().catch(() => ({}));
    if (!r.ok || !data.token) {
      msg.textContent = data?.error?.message || '登录失败';
      msg.className = 'status err';
      return;
    }
    api.serverUrl = getCurrentServerUrl();
    api.token = data.token;
    api.refreshToken = typeof data.refreshToken === 'string' ? data.refreshToken : '';
    api.username = data.username || user;
    saveSession();
    renderLoggedIn(mode === 'register' ? '注册成功，正在加载设备…' : '登录成功，正在加载设备…');
    void loadAuthSessions();
    void loadSecurityActivity();
    void syncPersistedFileTasks();
    // 账号已经可用，先释放登录按钮；设备列表在管理中心内独立加载，
    // 避免网络慢时把“登录成功”误呈现为按钮卡住。
    void loadDevices();
  } catch (e) {
    msg.textContent = e instanceof DOMException && e.name === 'AbortError'
      ? '请求超时，请稍后重试'
      : e instanceof Error && e.message !== 'Failed to fetch'
      ? e.message
      : '暂时无法连接，请稍后重试';
    msg.className = 'status err';
  } finally {
    window.clearTimeout(timeout);
    appState.authBusy = false;
    setAuthBusy(false);
  }
}

function accountLogin(): Promise<void> {
  return accountAuth('login');
}

function accountRegister(): Promise<void> {
  return accountAuth('register');
}

async function deleteAccount(): Promise<void> {
  if (!api.token || !api.username) {
    renderLoggedOut('请先登录账号', 'error');
    return;
  }
  const password = ($('account-delete-password') as HTMLInputElement).value;
  const confirmation = ($('account-delete-confirmation') as HTMLInputElement).value.trim();
  const message = $('account-delete-msg');
  if (!password) {
    message.textContent = '请输入当前密码';
    message.className = 'status error';
    ($('account-delete-password') as HTMLInputElement).focus();
    return;
  }
  if (confirmation !== api.username) {
    message.textContent = '请输入完全一致的账号名称';
    message.className = 'status error';
    ($('account-delete-confirmation') as HTMLInputElement).focus();
    return;
  }
  if (!window.confirm('删除账号后将退出所有设备，并清除设备关联。此操作无法撤销，确定继续吗？')) return;
  const button = $('account-delete-btn') as HTMLButtonElement;
  button.disabled = true;
  button.setAttribute('aria-busy', 'true');
  message.textContent = '正在删除账号…';
  message.className = 'status';
  try {
    const result = await api.deleteAccount(password, confirmation);
    if (!result.ok) {
      message.textContent = result.error || '账号删除失败，请重试';
      message.className = 'status error';
      return;
    }
    clearSession();
    ($('account-delete-password') as HTMLInputElement).value = '';
    ($('account-delete-confirmation') as HTMLInputElement).value = '';
    renderLoggedOut('账号已删除，本机登录状态已清除', 'ok');
    setAccountTab();
  } catch {
    message.textContent = '账号删除失败，请稍后重试';
    message.className = 'status error';
  } finally {
    button.disabled = false;
    button.setAttribute('aria-busy', 'false');
  }
}

export {
  saveSession,
  clearSession,
  readSession,
  setAccountState,
  setAccountTab,
  renderLoggedOut,
  renderLoggedIn,
  setAuthBusy,
  accountLogout,
  loadAuthSessions,
  loadSecurityActivity,
  revokeOtherAuthSessions,
  accountAuth,
  accountLogin,
  accountRegister,
  deleteAccount,
};
